/**
 * Promise-based client for the MuPDF page-render worker (VE-27065).
 *
 * The same worker also serves the non-render MuPDF jobs: the searchability raster
 * probe (`classifyPdfPage`, VE-27250), the text pre-pass (`extractMupdfText`,
 * VE-26267), the whole-document page-size pass (`extractMupdfDims`) and the
 * Redact Page scan (`scanMupdfPageElements`, VE-25621) — those
 * run here because MuPDF is far faster in-browser than pdf.js, and because each
 * one it absorbs is one fewer separate pdf.js document (i.e. one fewer full
 * download of the file plus its own worker).
 *
 * - Spawns the worker lazily on the first render request, so the ~10 MB mupdf
 *   WASM only loads when the viewer actually needs a MuPDF-rendered page.
 * - Reused across renders within a doc-editor session; the WASM + the opened
 *   document stay warm.
 * - Requests are correlated by an auto-incrementing id.
 * - PRIORITY QUEUE: the worker is single-threaded, so the client keeps only a
 *   couple of requests in flight and always dispatches the highest-priority
 *   queued job next. That lets a main-page render preempt the thumbnail rail's
 *   background whole-document sweep (VE-27104) — otherwise every main-page render
 *   would wait behind hundreds of thumbnail renders already sitting in the
 *   worker's own FIFO, so a fast scroll on the main panel showed the placeholder
 *   (or a blank page) for a second or two.
 * - ONE IN-FLIGHT JOB: a dispatched job can't be preempted, so every extra slot
 *   adds a whole job to an interactive render's worst case. Measured, a second
 *   slot cost a page render a mean 635 ms of queue wait while buying only a
 *   sub-millisecond message hop.
 * - LOW FAIRNESS: strict priority, except that after LOW_STARVATION_LIMIT
 *   dispatches that passed a WAITING LOW job over, the slot goes to it. With two
 *   slots LOW had a reserved one; with a single slot fairness has to be a share
 *   of dispatches. It is load-bearing: the probe pass keeps 8 CLASSIFY jobs
 *   outstanding, which under strict priority would starve the rail for a whole
 *   scanned document. (VE-27261's original rule — LOW only when wholly idle —
 *   starved it even worse, since the pre-pass never leaves the worker idle.)
 *   The share is BOUNDED to CLASSIFY-tier work and never preempts an
 *   interactive job; unbounded it reintroduced the very second-job wait that
 *   one in-flight slot exists to remove.
 * - VISIBLE THUMBNAILS PREEMPT INDEXING: thumbnails actually on screen are
 *   dispatched at MUPDF_PRIORITY_THUMB_VISIBLE, above the text pre-pass, so the
 *   rail fills where the user is looking while the rest of it sweeps at LOW.
 * - CANCELLATION (VE-27261): renders accept an optional AbortSignal; aborting
 *   removes a STILL-QUEUED render (in-flight ones can't be interrupted). A fast
 *   scroll cancels the renders for pages it flew past so the page you land on
 *   isn't stuck behind a backlog of them.
 *
 * - SHARES THE DOCUMENT BYTES: the worker hands the file it fetched to the main
 *   thread (`documentBytes`), so pdf.js can be given `file={{ data }}` instead of
 *   downloading the same 5.3 MB again. The two used to fetch independently and in
 *   parallel, splitting the pipe at ~2 MB/s each.
 * - INSTRUMENTED: every job's queue wait and service time is recorded per
 *   priority tier and read back via `mupdfQueueStats()` (surfaced as
 *   `redactQueueDump()` by perfProbe). Unconditional, so it works in a
 *   production build — it exists to compare the same document across machines.
 *
 * Mirrors burnInWorkerClient's lazy-spawn + id-correlated pending pattern.
 */
import {
  recordMilestone,
  recordSpan,
  registerSampleReset,
} from './mainThreadSpans';
import type { BoundingBox } from '@redact-modules/documents/models';
import type {
  MupdfDocBytes,
  MupdfDocTiming,
  MupdfRenderRequest,
  MupdfWarmRequest,
  MupdfClassifyRequest,
  MupdfDimsRequest,
  MupdfExtractRequest,
  MupdfPageElementsRequest,
  MupdfPageText,
  MupdfRenderResponse,
} from './mupdfRenderWorker';

export type { MupdfPageText, MupdfTextRun } from './mupdfRenderWorker';

export interface MupdfRenderResult {
  bitmap: ImageBitmap;
  width: number;
  height: number;
}

export interface MupdfClassifyResult {
  /** The page rasterizes to non-uniform pixels — a scan/graphics, not blank. */
  hasContent: boolean;
}

/** Text pre-pass result (VE-26267): a range of pages' extracted text geometry. */
export interface MupdfExtractResult {
  pages: MupdfPageText[];
  /** Total pages in the document — lets the caller stream ranges to the end. */
  totalPages: number;
}

/** Every page's size in points, index 0 = page 1 (/Rotate already applied). */
export interface MupdfDimsResult {
  pages: Array<{ width: number; height: number }>;
}

type MupdfResult =
  | MupdfRenderResult
  | MupdfClassifyResult
  | MupdfExtractResult
  | MupdfDimsResult
  | BoundingBox[];

/** Lower number = dispatched sooner. */
export const MUPDF_PRIORITY_HIGH = 0; // main editor page renders, doc warm
export const MUPDF_PRIORITY_THUMB_VISIBLE = 3; // thumbnails currently ON SCREEN
export const MUPDF_PRIORITY_CLASSIFY = 5; // open-time searchability probe
export const MUPDF_PRIORITY_LOW = 10; // thumbnail rail background sweep

/**
 * How many requests may sit in the (single-threaded) worker at once.
 *
 * ONE. A dispatched job cannot be preempted, and anything already handed to the
 * worker is beyond the client's reordering — so every extra in-flight slot adds
 * a whole job to the worst case for an interactive render. This was 2, on the
 * reasoning that a second slot keeps the worker busy during the client↔worker
 * message hop; measured, that hop is sub-millisecond against jobs of 250-550 ms,
 * while the second slot cost a page render a MEASURED mean 635 ms of queue wait
 * (clicking next-page quickly felt laggy for exactly this reason). One slot also
 * makes `service` a clean per-job number instead of one polluted by whatever
 * else was in the worker.
 */
const MAX_INFLIGHT = 1;

/**
 * After this many consecutive non-LOW dispatches, the next slot goes to a queued
 * LOW job (the background thumbnail sweep).
 *
 * With two slots, LOW got a *reserved* slot. One slot cannot reserve anything, so
 * fairness has to be a share of dispatches instead — and it is genuinely needed,
 * not defensive: the searchability probe pass keeps up to PROBE_CONCURRENCY (8)
 * CLASSIFY jobs outstanding, so on a fully scanned document strict priority
 * ordering would starve the rail for the entire probe pass. This guarantees LOW
 * at least one dispatch in four.
 */
const LOW_STARVATION_LIMIT = 3;

interface PendingRequest {
  resolve: (result: MupdfResult | void) => void;
  reject: (error: Error) => void;
  // Queue instrumentation (see recordCompletion).
  enqueuedAt: number;
  startedAt: number;
  label: string;
  priority: number;
}

interface QueuedJob extends PendingRequest {
  req:
    | MupdfRenderRequest
    | MupdfWarmRequest
    | MupdfClassifyRequest
    | MupdfExtractRequest
    | MupdfDimsRequest;
  priority: number;
}

interface DocBytesSlot {
  buffer: ArrayBuffer | null;
  /** Waiters for this url's bytes. */
  waiting: Array<{
    resolve: (buffer: ArrayBuffer) => void;
    reject: (err: Error) => void;
  }>;
}

/**
 * Document bytes the worker fetched, keyed by url, so pdf.js can reuse them
 * instead of downloading the same file again.
 *
 * KEYED, and capped at two — not a single slot. A single slot deadlocked: the
 * worker posts a url's bytes exactly ONCE (its own cache is permanent), so once
 * the slot had been replaced — which the Redacted tab and Side-by-Side do
 * routinely, by warming the redacted url — a later request for the first document
 * could never be answered. It never resolved and never rejected, so the canvas
 * showed "Loading PDF…" for the full BYTE_SHARE_TIMEOUT_MS and then downloaded
 * the file again: strictly worse than not sharing at all. Two entries is the
 * documented normal load (source + redacted).
 */
const docBytesByUrl = new Map<string, DocBytesSlot>();
const DOC_BYTES_LIMIT = 2;

function bytesSlotFor(url: string): DocBytesSlot {
  const existing = docBytesByUrl.get(url);
  if (existing) {
    return existing;
  }
  const slot: DocBytesSlot = { buffer: null, waiting: [] };
  docBytesByUrl.set(url, slot);
  // Evict the oldest FULFILLED slot; never one with waiters, which is the
  // deadlock this replaced.
  if (docBytesByUrl.size > DOC_BYTES_LIMIT) {
    for (const [key, candidate] of docBytesByUrl) {
      if (key !== url && candidate.waiting.length === 0) {
        docBytesByUrl.delete(key);
        break;
      }
    }
  }
  return slot;
}

/**
 * The document's bytes, as fetched by the worker. Resolves when they land.
 *
 * This is how pdf.js avoids a second download of the same file: react-pdf takes
 * `file={{ data }}`, so the canvas can wait for these rather than handing it a
 * URL. It does NOT trigger a fetch on its own — the caller should already have
 * warmed the document (DocumentCanvas does, on mount), and a fetch happens for
 * the first job that needs the doc regardless.
 *
 * Never resolves if the worker is disposed before the bytes arrive; callers must
 * treat it as best-effort and keep a URL fallback.
 */
export function documentBytes(pdfUrl: string): Promise<ArrayBuffer> {
  const slot = bytesSlotFor(pdfUrl);
  if (slot.buffer) {
    return Promise.resolve(slot.buffer);
  }
  return new Promise<ArrayBuffer>((resolve, reject) => {
    slot.waiting.push({ resolve, reject });
  });
}

let worker: Worker | null = null;
const pending = new Map<number, PendingRequest>();
const queue: QueuedJob[] = [];
let inFlight = 0;
// How many of `inFlight` are LOW-priority. Reported by the queue probe; with one
// slot it is only ever 0 or 1.
let inFlightLow = 0;
// Consecutive non-LOW dispatches, for the LOW_STARVATION_LIMIT share.
let consecutiveNonLow = 0;
let nextId = 1;

// ---------------------------------------------------------------------------
// Queue instrumentation
//
// Finishes the diagnostic VE-27261 started: every job already carried
// `enqueuedAt`/`startedAt`, but nothing ever read them. Split per priority tier,
// the two halves answer different questions:
//   - WAIT (enqueue -> dispatch) is SCHEDULING. A large HIGH wait means an
//     interactive render sat behind background work; a large LOW wait means the
//     thumbnail sweep is being starved.
//   - SERVICE (dispatch -> response) is raw MuPDF THROUGHPUT, so it is the
//     number to compare across machines — the same document on a slower CPU
//     shows the difference here and nowhere else.
//
// Recording is unconditional rather than gated behind the perfProbe localStorage
// flags, because the point is to be able to read it off a PRODUCTION build on
// someone else's machine. The cost is a few arithmetic ops per job plus a fixed
// ~64 KB of ring buffers. Dump it with `redactQueueDump()` (see perfProbe.ts).
// ---------------------------------------------------------------------------

/** Ring-buffer length, per tier per metric — bounds memory over a long session. */
const QUEUE_SAMPLE_LIMIT = 1000;

const LOW_TIER_NAME = 'LOW  (background thumbnail sweep)';

// Ordered best-priority first; a job belongs to the first tier whose bound it
// satisfies. Kept as display order for the dump.
const QUEUE_TIERS: ReadonlyArray<{ name: string; maxPriority: number }> = [
  { name: 'HIGH (page render / doc warm)', maxPriority: MUPDF_PRIORITY_HIGH },
  {
    name: 'THUMB (on-screen thumbnail)',
    maxPriority: MUPDF_PRIORITY_THUMB_VISIBLE,
  },
  {
    name: 'INDEX (text pre-pass / probe)',
    maxPriority: MUPDF_PRIORITY_CLASSIFY,
  },
  { name: LOW_TIER_NAME, maxPriority: Number.POSITIVE_INFINITY },
];

function tierName(priority: number): string {
  for (const t of QUEUE_TIERS) {
    if (priority <= t.maxPriority) {
      return t.name;
    }
  }
  return LOW_TIER_NAME;
}

interface TierAccumulator {
  completed: number;
  failed: number;
  cancelled: number;
  wait: Float64Array;
  service: Float64Array;
  /** Samples written; > QUEUE_SAMPLE_LIMIT means the ring has wrapped. */
  written: number;
}

const statsByTier = new Map<string, TierAccumulator>();

function tierStats(priority: number): TierAccumulator {
  const name = tierName(priority);
  const existing = statsByTier.get(name);
  if (existing) {
    return existing;
  }
  const fresh: TierAccumulator = {
    completed: 0,
    failed: 0,
    cancelled: 0,
    wait: new Float64Array(QUEUE_SAMPLE_LIMIT),
    service: new Float64Array(QUEUE_SAMPLE_LIMIT),
    written: 0,
  };
  statsByTier.set(name, fresh);
  return fresh;
}

/**
 * Record one finished job. Timing samples are kept only for SUCCESSES: a worker
 * crash rejects every in-flight and queued request at once, and those bogus
 * service times would swamp the percentiles. Failures are counted instead.
 */
function recordCompletion(handler: PendingRequest, ok: boolean): void {
  const now = performance.now();
  const t = tierStats(handler.priority);
  if (!ok) {
    t.failed++;
    return;
  }
  const idx = t.written % QUEUE_SAMPLE_LIMIT;
  t.wait[idx] = handler.startedAt - handler.enqueuedAt;
  t.service[idx] = now - handler.startedAt;
  t.written++;
  t.completed++;
}

export interface MupdfQueueMetric {
  n: number;
  meanMs: number;
  medianMs: number;
  p90Ms: number;
  maxMs: number;
}

export interface MupdfQueueTierStats {
  tier: string;
  completed: number;
  failed: number;
  cancelled: number;
  /** Enqueue -> dispatch. Scheduling delay. */
  waitMs: MupdfQueueMetric;
  /** Dispatch -> response. MuPDF throughput. */
  serviceMs: MupdfQueueMetric;
}

export interface MupdfQueueSnapshot {
  tiers: MupdfQueueTierStats[];
  /** Jobs queued but not yet dispatched, right now. */
  queueDepth: number;
  inFlight: number;
  inFlightLow: number;
}

function metric(buf: Float64Array, written: number): MupdfQueueMetric {
  const n = Math.min(written, QUEUE_SAMPLE_LIMIT);
  if (n === 0) {
    return { n: 0, meanMs: 0, medianMs: 0, p90Ms: 0, maxMs: 0 };
  }
  const sorted = Array.from(buf.subarray(0, n)).sort((a, b) => a - b);
  const sum = sorted.reduce((acc, x) => acc + x, 0);
  const at = (q: number): number =>
    sorted[Math.min(n - 1, Math.floor(q * n))] ?? 0;
  const round = (x: number): number => Math.round(x * 100) / 100;
  return {
    n,
    meanMs: round(sum / n),
    medianMs: round(at(0.5)),
    p90Ms: round(at(0.9)),
    maxMs: round(sorted[n - 1] ?? 0),
  };
}

/**
 * Per-tier queue-wait and service-time snapshot, plus the queue's live depth.
 * Tiers with no activity are omitted. Safe to call at any time.
 */
export function mupdfQueueStats(): MupdfQueueSnapshot {
  const tiers: MupdfQueueTierStats[] = [];
  for (const { name } of QUEUE_TIERS) {
    const t = statsByTier.get(name);
    if (!t) {
      continue;
    }
    tiers.push({
      tier: name,
      completed: t.completed,
      failed: t.failed,
      cancelled: t.cancelled,
      waitMs: metric(t.wait, t.written),
      serviceMs: metric(t.service, t.written),
    });
  }
  return { tiers, queueDepth: queue.length, inFlight, inFlightLow };
}

/** Drop every recorded sample (e.g. to time one specific interaction). */
export function resetMupdfQueueStats(): void {
  statsByTier.clear();
}

// Clear these alongside the main-thread buffers whenever a document opens, so
// a dump describes one document. See registerSampleReset for why the dependency
// points this way instead of the saga importing this module.
registerSampleReset(resetMupdfQueueStats);

function getWorker(): Worker {
  if (worker) {
    return worker;
  }
  const w = new Worker(new URL('./mupdfRenderWorker.ts', import.meta.url), {
    type: 'module',
  });
  w.addEventListener('message', (e: MessageEvent<MupdfRenderResponse>) => {
    const res = e.data;
    // Out-of-band bytes delivery: not a reply, so it has no pending entry.
    if ('docBytes' in res) {
      const d = res as unknown as MupdfDocBytes;
      const slot = bytesSlotFor(d.url);
      slot.buffer = d.buffer;
      for (const waiter of slot.waiting.splice(0)) {
        waiter.resolve(d.buffer);
      }
      return;
    }
    // Out-of-band document-open timing: not a reply, so it has no pending entry.
    if ('docTiming' in res) {
      const t = res as unknown as MupdfDocTiming;
      // Two reports per document now: one when the bytes land (fetchMs set) and
      // one when it is parsed (parseMs set), because those no longer happen
      // together — the download starts before the WASM has loaded.
      if (t.fetchMs > 0) {
        recordSpan('mupdf doc: fetch', t.fetchMs);
        recordMilestone(
          `mupdf bytes …${t.urlTail} (${Math.round(t.bytes / 1024)}KB in ` +
            `${t.fetchMs}ms${t.fromCache ? ', from cache' : ''})`
        );
      }
      if (t.parseMs > 0) {
        recordSpan('mupdf doc: openDocument', t.parseMs);
        recordMilestone(
          `mupdf doc #${t.opensThisWorker} parsed in ${t.parseMs}ms ` +
            `(wasm load ${t.wasmLoadMs}ms)`
        );
      }
      return;
    }
    const handler = pending.get(res.id);
    if (!handler) {
      return;
    }
    pending.delete(res.id);
    inFlight--;
    if (handler.priority >= MUPDF_PRIORITY_LOW) {
      inFlightLow--;
    }
    recordCompletion(handler, res.ok);
    if (res.ok && 'warmed' in res) {
      handler.resolve();
    } else if (res.ok && 'classified' in res) {
      handler.resolve({ hasContent: res.hasContent });
    } else if (res.ok && 'dims' in res) {
      handler.resolve({ pages: res.pages });
    } else if (res.ok && 'pageElements' in res) {
      handler.resolve(res.boxes);
    } else if (res.ok && 'extracted' in res) {
      handler.resolve({ pages: res.pages, totalPages: res.totalPages });
    } else if (res.ok) {
      handler.resolve({
        bitmap: res.bitmap,
        width: res.width,
        height: res.height,
      });
    } else {
      handler.reject(new Error(res.message));
    }
    pump();
  });
  w.addEventListener('error', (e) => {
    if (worker !== w) {
      // A stale error from a worker we've already replaced. Without this guard
      // the handler below nulls out the LIVE worker, which is then never
      // terminated, and the next request pays another ~10 MB WASM spawn.
      return;
    }
    // Worker-level error (e.g. WASM failed to load) rejects every in-flight and
    // queued request — they can't make forward progress on a dead worker.
    const message = e.message || 'MuPDF render worker crashed';
    for (const [, handler] of pending) {
      // Count it: `failed` is exactly the column you read when the rail is
      // stranding gray frames, and it stayed 0 through a crash before this.
      recordCompletion(handler, false);
      handler.reject(new Error(message));
    }
    for (const job of queue) {
      job.reject(new Error(message));
    }
    pending.clear();
    queue.length = 0;
    inFlight = 0;
    inFlightLow = 0;
    consecutiveNonLow = 0;
    // Terminate the crashed worker so its thread doesn't linger; the next
    // render/warm spawns a fresh one via getWorker().
    w.terminate();
    worker = null;
  });
  worker = w;
  return w;
}

// Dispatch the highest-priority queued jobs until the in-flight cap is reached.
function pump(): void {
  while (inFlight < MAX_INFLIGHT && queue.length > 0) {
    // LOW FAIRNESS: normally strict priority (so LOW runs only when nothing
    // better is queued), except that after LOW_STARVATION_LIMIT dispatches that
    // passed a waiting LOW job over, the slot is handed to it. Without that
    // share the probe pass — which keeps 8 CLASSIFY jobs outstanding — would
    // starve the thumbnail sweep for a whole scanned document.
    //
    // The share is bounded to CLASSIFY-tier work and NEVER preempts INTERACTIVE
    // work (HIGH page renders, on-screen thumbnails). Without that bound the
    // override reintroduced precisely the cost MAX_INFLIGHT = 1 exists to
    // remove: a queued page render could be skipped in favour of a background
    // mint, so it waited for the in-flight job PLUS a whole thumbnail — the
    // second-job wait, spelled differently. Indexing is what starves the rail;
    // the user's own navigation is not.
    const isLow = (j: QueuedJob): boolean => j.priority >= MUPDF_PRIORITY_LOW;
    const hasLow = queue.some(isLow);
    const hasInteractive = queue.some(
      (j) => j.priority < MUPDF_PRIORITY_CLASSIFY
    );
    const forceLow =
      hasLow && !hasInteractive && consecutiveNonLow >= LOW_STARVATION_LIMIT;
    let bestIdx = -1;
    let bestPriority = Number.POSITIVE_INFINITY;
    for (let i = 0; i < queue.length; i++) {
      const cand = queue[i];
      if (!cand || (forceLow && !isLow(cand))) {
        continue;
      }
      if (cand.priority < bestPriority) {
        bestPriority = cand.priority;
        bestIdx = i;
      }
    }
    if (bestIdx < 0) {
      return; // nothing eligible (only reachable if the queue emptied under us)
    }
    const job = queue.splice(bestIdx, 1)[0];
    if (!job) {
      return;
    }
    job.startedAt = performance.now();
    pending.set(job.req.id, {
      resolve: job.resolve,
      reject: job.reject,
      enqueuedAt: job.enqueuedAt,
      startedAt: job.startedAt,
      label: job.label,
      priority: job.priority,
    });
    inFlight++;
    if (job.priority >= MUPDF_PRIORITY_LOW) {
      inFlightLow++;
      consecutiveNonLow = 0;
    } else if (hasLow) {
      // Only a dispatch that actually passed a WAITING LOW job over counts
      // toward starvation. Counting unconditionally let the tally grow without
      // bound while the rail was collapsed (no LOW queued at all), so the first
      // mint after re-expanding it jumped the entire queue.
      consecutiveNonLow++;
    }
    getWorker().postMessage(job.req);
  }
}

/**
 * Drop a still-queued (not yet dispatched) render, rejecting its promise. No-op
 * if the job has already started — the single-threaded worker can't be
 * interrupted mid-render, so the caller just ignores that result. This is what
 * lets a fast scroll cancel the renders for pages it flew past, instead of
 * making the page you land on wait behind a backlog of them (VE-27261).
 */
function cancelQueued(id: number): void {
  const idx = queue.findIndex((j) => j.req.id === id);
  if (idx >= 0) {
    const job = queue.splice(idx, 1)[0];
    if (job) {
      tierStats(job.priority).cancelled++;
      job.reject(new DOMException('Render cancelled', 'AbortError'));
    }
  }
}

function enqueue(
  req:
    | MupdfRenderRequest
    | MupdfWarmRequest
    | MupdfClassifyRequest
    | MupdfExtractRequest
    | MupdfDimsRequest
    | MupdfPageElementsRequest,
  priority: number,
  resolve: (result: MupdfResult | void) => void,
  reject: (error: Error) => void
): void {
  // TEMP diagnostic (VE-27261): tag each job so completion logs are readable.
  const label =
    'warm' in req
      ? `warm`
      : 'dims' in req
        ? `dims`
        : 'classify' in req
          ? `classify:p${req.pageIndex + 1}`
          : 'extractText' in req
            ? `extract:p${req.startPage + 1}-${req.endPage + 1}`
            : 'pageElements' in req
              ? `elements:p${req.pageIndex + 1}`
              : `p${req.pageIndex + 1}${req.targetWidthPx ? `@${req.targetWidthPx}w` : `@${req.scale ?? '?'}x`}`;
  // A new request means someone still wants the worker: cancel a pending
  // release so a remount (or another consumer) doesn't lose its work.
  cancelPendingDispose();
  queue.push({
    req,
    priority,
    resolve,
    reject,
    enqueuedAt: performance.now(),
    startedAt: 0,
    label,
  });
  pump();
}

/**
 * Render one page to an ImageBitmap. `scale` is pixels-per-point (the page's CSS
 * scale × devicePixelRatio), so the bitmap matches the on-screen backing store.
 * High priority by default (main editor pages preempt the thumbnail sweep).
 */
export function renderPdfPage(
  pdfUrl: string,
  pageIndex: number,
  scale: number,
  priority: number = MUPDF_PRIORITY_HIGH,
  signal?: AbortSignal
): Promise<MupdfRenderResult> {
  const id = nextId++;
  return new Promise<MupdfRenderResult>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Render cancelled', 'AbortError'));
      return;
    }
    const req: MupdfRenderRequest = { id, pdfUrl, pageIndex, scale };
    enqueue(req, priority, (r) => resolve(r as MupdfRenderResult), reject);
    // Cancel the queued render if the caller aborts (page scrolled out of the
    // window before it was dispatched) — keeps the backlog to what's on screen.
    signal?.addEventListener('abort', () => cancelQueued(id), { once: true });
  });
}

/**
 * Render a page to an ImageBitmap at a target output width (device px), letting
 * the worker derive the scale from the page's own bounds. Lets a caller render
 * without knowing the page's native size up front — used by the thumbnail rail,
 * which would otherwise have to wait for the open-time pre-pass to publish
 * per-page dimensions (VE-27104). Low priority by default so the rail's
 * background sweep yields the worker to main-page renders.
 */
export function renderPdfPageToWidth(
  pdfUrl: string,
  pageIndex: number,
  targetWidthPx: number,
  priority: number = MUPDF_PRIORITY_LOW,
  signal?: AbortSignal
): Promise<MupdfRenderResult> {
  const id = nextId++;
  return new Promise<MupdfRenderResult>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Render cancelled', 'AbortError'));
      return;
    }
    const req: MupdfRenderRequest = { id, pdfUrl, pageIndex, targetWidthPx };
    enqueue(req, priority, (r) => resolve(r as MupdfRenderResult), reject);
    signal?.addEventListener('abort', () => cancelQueued(id), { once: true });
  });
}

/**
 * Searchability probe for a text-less page: {hasContent} — does it paint a
 * scan/graphics (→ unsearchable) or is it genuinely blank? Derived from a tiny
 * grayscale raster's tonal spread in the worker, so it avoids decoding the
 * full-res image (VE-27250). Priority sits above the thumbnail sweep so the
 * open-time "N of K pages unsearchable" count fills in promptly, but below main
 * page renders; each probe is a tiny raster so it barely delays the sweep.
 */
export function classifyPdfPage(
  pdfUrl: string,
  pageIndex: number,
  priority: number = MUPDF_PRIORITY_CLASSIFY
): Promise<MupdfClassifyResult> {
  const id = nextId++;
  return new Promise<MupdfClassifyResult>((resolve, reject) => {
    const req: MupdfClassifyRequest = { id, pdfUrl, pageIndex, classify: true };
    enqueue(req, priority, (r) => resolve(r as MupdfClassifyResult), reject);
  });
}

/**
 * Extract text + per-character geometry for a range of pages [startPage,
 * endPage] (0-based, inclusive) via MuPDF — the pre-pass text source (VE-26267).
 * Returns each page's glyph runs + native size, and the document's total page
 * count so the caller can stream ranges to the end. CLASSIFY priority: fills the
 * search index promptly but yields the worker to interactive page renders.
 */
export function extractMupdfText(
  pdfUrl: string,
  startPage: number,
  endPage: number,
  priority: number = MUPDF_PRIORITY_CLASSIFY
): Promise<MupdfExtractResult> {
  const id = nextId++;
  return new Promise<MupdfExtractResult>((resolve, reject) => {
    const req: MupdfExtractRequest = {
      id,
      pdfUrl,
      startPage,
      endPage,
      extractText: true,
    };
    enqueue(req, priority, (r) => resolve(r as MupdfExtractResult), reject);
  });
}

/**
 * Every page's size, in ONE message (VE-27261 follow-up). The virtualized
 * viewers need all of them up front — until a page's real size is known its
 * spacer falls back to DEFAULT_ASPECT, so a far scroll or a jump lays out from
 * the wrong heights and then reflows as pages render (the current-page indicator
 * visibly drifts, and the render window thrashes against moving geometry).
 *
 * HIGH priority: it is a one-time ~17 ms of worker time for a 1600-page document
 * (measured) and correct layout gates everything the user does, so it should not
 * queue behind the text pre-pass.
 *
 * This replaced a second, pdf.js-based document that existed only to read
 * `getViewport({scale: 1})` per page — which meant another full download of the
 * file and another pdf.js worker, and was ~3.5x slower at the page walk besides.
 */
export function extractMupdfDims(
  pdfUrl: string,
  priority: number = MUPDF_PRIORITY_HIGH
): Promise<MupdfDimsResult> {
  const id = nextId++;
  return new Promise<MupdfDimsResult>((resolve, reject) => {
    const req: MupdfDimsRequest = { id, pdfUrl, dims: true };
    enqueue(req, priority, (r) => resolve(r as MupdfDimsResult), reject);
  });
}

/**
 * Redact Page boxes for one page (VE-25621): the worker scans the page and runs
 * `documents/pageElements.ts` on it, returning one box per element.
 *
 * HIGH priority — the user clicked and is waiting on a spinner — at a cost: it
 * decodes the page's images (up to ~2s on a heavy scan) and a dispatched job
 * can't be preempted, so page renders queue behind it for that long. Never
 * issued from a pre-pass.
 */
export function scanMupdfPageElements(
  pdfUrl: string,
  pageIndex: number,
  priority: number = MUPDF_PRIORITY_HIGH
): Promise<BoundingBox[]> {
  const id = nextId++;
  return new Promise<BoundingBox[]>((resolve, reject) => {
    const req: MupdfPageElementsRequest = {
      id,
      pdfUrl,
      pageIndex,
      pageElements: true,
    };
    enqueue(req, priority, (r) => resolve(r as BoundingBox[]), reject);
  });
}

/**
 * Open + parse a document ahead of the first render. This pays MuPDF's
 * file-size-proportional `openDocument` cost (plus the one-time ~10 MB WASM
 * load, since it spawns the worker) at document-open, in parallel with pdf.js's
 * own parse — so the first page mount hits the warm-doc fast path instead of
 * waiting on a cold worker + full-file parse (VE-27065). Best-effort and
 * idempotent: a warm for the already-warm doc is cheap, and a failure is
 * swallowed (the render path retries and surfaces its own errors).
 */
export function warmMupdfDocument(pdfUrl: string): Promise<void> {
  const id = nextId++;
  return new Promise<void>((resolve) => {
    const req: MupdfWarmRequest = { id, pdfUrl, warm: true };
    enqueue(
      req,
      MUPDF_PRIORITY_HIGH,
      () => resolve(),
      () => resolve()
    );
  });
}

/**
 * How long a release waits before actually tearing the worker down. Any new
 * request inside the window cancels it.
 *
 * The worker is SHARED — the editor canvas, the thumbnail rail, the text pre-pass
 * saga and the dims saga all use it — so disposing it rejects their in-flight
 * work too. Under StrictMode (dev) React mounts, unmounts and remounts every
 * component, so an immediate dispose on the canvas's cleanup fires during
 * document OPEN: measured on a 1602-page file, that killed the dims request
 * outright (no page dimensions, so no page could raster) and sent the text
 * pre-pass into its retry backoff — first text chunk indexed at 11.0s, INDEX
 * queue wait peaking at 10.3s. A grace period makes the teardown survive a
 * remount without changing the steady-state behaviour.
 */
const DISPOSE_GRACE_MS = 2000;
let disposeTimer: ReturnType<typeof setTimeout> | null = null;

function cancelPendingDispose(): void {
  if (disposeTimer !== null) {
    clearTimeout(disposeTimer);
    disposeTimer = null;
  }
}

/**
 * Release the worker: tear it down after DISPOSE_GRACE_MS unless another request
 * arrives first. This is what a component should call on unmount — an immediate
 * teardown races StrictMode's remount and any other consumer's in-flight work.
 */
export function releaseMupdfRenderWorker(): void {
  cancelPendingDispose();
  disposeTimer = setTimeout(() => {
    disposeTimer = null;
    disposeMupdfRenderWorker();
  }, DISPOSE_GRACE_MS);
}

/**
 * Tear the render worker down NOW (frees the ~10 MB WASM heap + the warm
 * document), rejecting anything outstanding. Prefer `releaseMupdfRenderWorker`
 * from component cleanup; this is for explicit teardown and tests.
 */
export function disposeMupdfRenderWorker(): void {
  cancelPendingDispose();
  if (worker) {
    worker.terminate();
    worker = null;
  }
  const dead = new Error('MuPDF render worker disposed');
  for (const [, handler] of pending) {
    // Counted, like the crash path: a job killed by a teardown was invisible in
    // `failed`, which made a dispose-induced delay indistinguishable from a slow
    // worker when reading the dump.
    recordCompletion(handler, false);
    handler.reject(dead);
  }
  for (const job of queue) {
    tierStats(job.priority).cancelled++;
    job.reject(dead);
  }
  pending.clear();
  queue.length = 0;
  inFlight = 0;
  inFlightLow = 0;
  consecutiveNonLow = 0;
  // REJECT waiters rather than dropping them: a dropped resolver is a promise
  // that never settles, and its only escape was a 15s timeout in the caller.
  // Rejecting lets the caller fall back to the url immediately.
  // And clear the bytes unconditionally — retaining them defeated the point of
  // dispose (it frees the ~10 MB WASM heap while holding a whole unredacted PDF
  // in module scope, past CLOSE_DOCUMENT and back to the folder view).
  for (const slot of docBytesByUrl.values()) {
    for (const waiter of slot.waiting.splice(0)) {
      waiter.reject(dead);
    }
  }
  docBytesByUrl.clear();
}
